import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ExternalLink, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field, inputCls, Markdown, Spinner } from "@/components/common";

const fmt = (d: string) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "");
const gold = "text-[#C9A96E]";

function Hero({ p }: any) {
  const [img, setImg] = useState(p.images?.[0] || "");
  return (
    <section className="max-w-[1300px] mx-auto px-5 md:px-12 pt-12 md:pt-20 pb-14 grid md:grid-cols-2 gap-10 md:gap-20 items-center">
      <div className="rise">
        {img && <img src={img} alt={p.title} className="w-full aspect-[4/5] object-cover" data-testid="prov-main-image" />}
        {p.images?.length > 1 && <div className="mt-3 grid grid-cols-5 gap-2">{p.images.map((u: string, i: number) => (
          <button key={u + i} onClick={() => setImg(u)} className={`aspect-square overflow-hidden border transition-opacity ${u === img ? "border-[#C9A96E]" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`prov-thumb-${i}`}><img src={u} alt="" className="w-full h-full object-cover" /></button>))}</div>}
      </div>
      <div className="rise [animation-delay:120ms]">
        <p className={`eyebrow text-[10px] ${gold}`}>Certificate of Provenance</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight" data-testid="prov-title">{p.title}</h1>
        <p className="mt-6 eyebrow text-xs tracking-[0.3em]" data-testid="prov-serial">Serial · {p.serial}</p>
        <div className="mt-8 flex items-center gap-3 border-y border-white/15 py-5">
          <ShieldCheck className={gold} size={28} strokeWidth={1.2} />
          <div><p className="text-sm" data-testid="prov-authentic">Authenticated by Somnio Atelier</p><p className="text-xs text-white/50 mt-0.5">Bearing the Somnio shield, metal purity mark and this unique serial.</p></div>
        </div>
        <dl className="mt-6 grid grid-cols-2 gap-6 text-sm">
          <div><dt className="eyebrow text-[10px] text-white/50">Registered to</dt><dd className="mt-1" data-testid="prov-owner">{p.owner}</dd></div>
          {p.commissioned_on && <div><dt className="eyebrow text-[10px] text-white/50">Commissioned</dt><dd className="mt-1" data-testid="prov-commissioned">{fmt(p.commissioned_on)}</dd></div>}
        </dl>
      </div>
    </section>
  );
}

function Specs({ p }: any) {
  const stones = (p.stones || []).filter((s: any) => s.stone);
  if (!p.metal && !stones.length && !p.igi_number) return null;
  return (
    <section className="max-w-[1300px] mx-auto px-5 md:px-12 py-14 grid md:grid-cols-3 gap-8" data-testid="prov-specs">
      <div className="border border-white/15 p-7">
        <p className="eyebrow text-[10px] text-white/50">Metal</p>
        <p className="font-display text-2xl mt-3" data-testid="prov-metal">{p.metal || "—"}</p>
        {p.hallmark && <p className="text-xs text-white/60 mt-2" data-testid="prov-hallmark">Hallmark: {p.hallmark}</p>}
      </div>
      <div className="border border-white/15 p-7">
        <p className="eyebrow text-[10px] text-white/50">Stones</p>
        {stones.length ? <ul className="mt-3 space-y-3">{stones.map((s: any, i: number) => (
          <li key={i} data-testid={`prov-stone-${i}`}><p className="font-display text-xl">{[s.carat && `${s.carat} ct`, s.shape, s.stone].filter(Boolean).join(" ")}</p>
            {(s.colour || s.clarity) && <p className="text-xs text-white/60">{[s.colour && `Colour ${s.colour}`, s.clarity && `Clarity ${s.clarity}`].filter(Boolean).join(" · ")}</p>}</li>))}</ul> : <p className="mt-3 text-white/60">—</p>}
      </div>
      <div className="border border-[#C9A96E]/50 p-7">
        <p className={`eyebrow text-[10px] ${gold}`}>IGI Certification</p>
        {p.igi_number ? <>
          <p className="font-display text-2xl mt-3" data-testid="prov-igi-number">{p.igi_number}</p>
          <a href={p.igi_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 eyebrow text-[10px] link-u" data-testid="prov-igi-link">Verify with IGI <ExternalLink size={12} /></a>
        </> : <p className="mt-3 text-sm text-white/60">Certification applies to stones of 0.3 ct or greater.</p>}
      </div>
    </section>
  );
}

function Story({ p }: any) {
  if (!p.story) return null;
  return (
    <section className="max-w-3xl mx-auto px-5 md:px-12 py-14 text-center" data-testid="prov-story">
      <p className={`eyebrow text-[10px] ${gold}`}>The Design Story</p>
      <div className="mt-6 text-white/80 leading-relaxed [&_p]:mb-4 [&_.prose-lux_*]:!text-white/80"><Markdown text={p.story} /></div>
    </section>
  );
}

function Gift({ p }: any) {
  if (!p.gift) return null;
  return (
    <section className="max-w-3xl mx-auto px-5 md:px-12 py-14 text-center" data-testid="prov-gift">
      <p className={`eyebrow text-[10px] ${gold}`}>A Gift, With Love</p>
      <blockquote className="mt-6 font-display italic text-2xl md:text-3xl font-light leading-snug whitespace-pre-line" data-testid="prov-gift-message">“{p.gift.message}”</blockquote>
      {p.gift.signed_as && <p className="mt-5 text-sm text-white/70" data-testid="prov-gift-signed">— {p.gift.signed_as}</p>}
      {p.gift.date && <p className="mt-1 text-xs text-white/40">{fmt(p.gift.date)}</p>}
    </section>
  );
}

function History({ p, onTransfer }: any) {
  return (
    <section className="max-w-3xl mx-auto px-5 md:px-12 py-14 pb-24" data-testid="prov-history">
      <p className={`eyebrow text-[10px] ${gold} text-center`}>Chain of Ownership</p>
      <ol className="mt-8 border-l border-white/20 ml-2 space-y-7">
        {(p.history || []).map((h: any, i: number) => (
          <li key={i} className="pl-6 relative" data-testid={`prov-history-${i}`}>
            <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#C9A96E]" />
            <p className="font-display text-xl">{h.owner}</p>
            <p className="text-xs text-white/50 mt-1">{h.kind === "commissioned" ? "Commissioned" : "Ownership transferred"} · {fmt(h.date)}</p>
          </li>
        ))}
      </ol>
      <div className="text-center mt-12">
        <button onClick={onTransfer} className="inline-flex items-center gap-2 border border-white/40 px-7 py-3 eyebrow text-[11px] hover:bg-white hover:text-neutral-900 transition-colors" data-testid="prov-transfer-open"><ArrowRightLeft size={14} />Transfer ownership</button>
        <p className="text-xs text-white/40 mt-3">Gifting or passing this piece down? Register it to its new keeper.</p>
      </div>
    </section>
  );
}

function TransferDialog({ open, onClose, token, hasEmail, ownerFirst }: any) {
  const [f, setF] = useState({ current_owner_email: "", new_owner_name: "", new_owner_email: "", gift_message: "", signed_as: ownerFirst || "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/provenance/${token}/transfer`, f); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="prov-transfer-dialog">
        <DialogTitle className="font-display text-2xl">Transfer ownership</DialogTitle>
        {sent ? <p className="text-sm" data-testid="prov-transfer-sent">We've emailed the registered owner a link to confirm. Once confirmed, the new owner will receive this piece's provenance by email.</p>
          : !hasEmail ? <p className="text-sm" data-testid="prov-transfer-contact">This piece doesn't have an owner email on file yet. Please <Link to="/pages/contact" className="underline">contact our team</Link> and we'll transfer it for you.</p>
          : <form onSubmit={submit} className="space-y-4">
            <Field label="Current owner's email (as registered)"><input type="email" required value={f.current_owner_email} onChange={set("current_owner_email")} className={inputCls} data-testid="prov-transfer-current" /></Field>
            <Field label="New owner's full name"><input required value={f.new_owner_name} onChange={set("new_owner_name")} className={inputCls} data-testid="prov-transfer-name" /></Field>
            <Field label="New owner's email"><input type="email" required value={f.new_owner_email} onChange={set("new_owner_email")} className={inputCls} data-testid="prov-transfer-email" /></Field>
            <Field label="Gift message (optional — shown on the certificate)"><textarea rows={3} maxLength={400} value={f.gift_message} onChange={set("gift_message")} placeholder="For your wedding day — may it carry our story forward." className={inputCls} data-testid="prov-transfer-gift" /></Field>
            {f.gift_message.trim() && <Field label="Signed"><input maxLength={60} value={f.signed_as} onChange={set("signed_as")} placeholder="With love, Mum & Dad" className={inputCls} data-testid="prov-transfer-signed" /></Field>}
            <button disabled={busy} className="w-full bg-neutral-900 text-white py-3 eyebrow text-[11px] hover:bg-[var(--c-primary)] transition-colors disabled:opacity-50" data-testid="prov-transfer-submit">{busy ? "Sending…" : "Send confirmation to owner"}</button>
          </form>}
      </DialogContent>
    </Dialog>
  );
}

export default function Provenance() {
  const { token = "" } = useParams();
  const [p, setP] = useState<any>(null);
  const [err, setErr] = useState("");
  const [tx, setTx] = useState(false);
  useEffect(() => { document.title = "Certificate of Provenance | Somnio.Co"; api.get(`/provenance/${token}`).then((r) => setP(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  if (err) return <div className="max-w-xl mx-auto px-5 py-32 text-center" data-testid="prov-not-found"><h1 className="font-display text-4xl">Piece not found</h1><p className="mt-4 text-sm text-neutral-600">{err}</p></div>;
  if (!p) return <Spinner />;
  return (
    <div className="bg-[#0F0F10] text-white" data-testid="provenance-page">
      <Hero p={p} /><Gift p={p} /><Specs p={p} /><Story p={p} /><History p={p} onTransfer={() => setTx(true)} />
      <TransferDialog open={tx} onClose={() => setTx(false)} token={token} hasEmail={p.has_owner_email} ownerFirst={p.owner === "Private owner" ? "" : p.owner.split(" ")[0]} />
    </div>
  );
}

export function ProvenanceConfirm() {
  const { ctoken = "" } = useParams();
  const [res, setRes] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => { setBusy(true); try { setRes((await api.post(`/provenance/confirm/${ctoken}`)).data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="max-w-xl mx-auto px-5 py-28 text-center" data-testid="prov-confirm-page">
      <ShieldCheck className="mx-auto text-[#C9A96E]" size={40} strokeWidth={1.2} />
      <h1 className="font-display text-4xl mt-6">Confirm ownership transfer</h1>
      {res ? <><p className="mt-5 text-sm" data-testid="prov-confirm-done">Done — this piece is now registered to {res.owner} and they've been emailed its provenance.</p>
        <Link to={`/provenance/${res.token}`} className="inline-block mt-8 underline eyebrow text-[11px]" data-testid="prov-confirm-view">View provenance</Link></>
        : err ? <p className="mt-5 text-sm text-red-700" data-testid="prov-confirm-error">{err}</p>
        : <><p className="mt-5 text-sm text-neutral-600">Once confirmed, the piece's chain of ownership is updated permanently.</p>
          <button onClick={confirm} disabled={busy} className="mt-8 bg-neutral-900 text-white px-8 py-3 eyebrow text-[11px] disabled:opacity-50" data-testid="prov-confirm-button">{busy ? "Confirming…" : "Confirm transfer"}</button></>}
    </div>
  );
}
